<script setup lang="ts">
import {nextTick, reactive, ref, useTemplateRef, watch} from "vue";
import markdownit from "markdown-it";
import {useToast} from "primevue/usetoast";
import {useConfirm} from "primevue/useconfirm";
import {Button, Drawer, Textarea, ProgressSpinner} from "@/Components/Library";
import Str from "@/Utils/Str";
import {getHistory, ask as askViaApi, clearHistory} from "@/Services/albie";

const md = markdownit();
const toast = useToast();
const confirm = useConfirm();

const modalVisible = defineModel<boolean>();

const state = reactive({
    loading: {
        history: false,
        response: false,
    },
});

const messages = ref<{role: "user" | "ai"; text?: string}[]>([]);
const prompt = ref("");
const promptField = useTemplateRef("promptField");

const loadHistory = async () => {
    try {
        state.loading.history = true;
        const {history} = await getHistory();
        messages.value = history || [];
    } catch (error) {
        console.error("Failed to load history:", error);
    } finally {
        state.loading.history = false;
    }
};

const clear = async () => {
    confirm.require({
        header: "Clear Chat History",
        message: "Are you sure you want to clear the chat history?",
        accept: async () => {
            try {
                state.loading.history = true;
                await clearHistory();
                messages.value = [];
                prompt.value = "";
                toast.add({
                    severity: "success",
                    summary: "Cleared",
                    detail: "Chat history has been cleared.",
                    life: 3000,
                });
            } catch (error) {
                console.error("Failed to clear history:", error);
            } finally {
                state.loading.history = false;
            }
        },
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
    });
};

const ask = async () => {
    if (!prompt.value.trim()) {
        toast.add({
            severity: "warn",
            summary: "Enter a prompt",
            detail: "Please write a prompt to ask Albie.",
            life: 3000,
        });
        return;
    }

    try {
        state.loading.response = true;
        const promptText = prompt.value;

        const data = await askViaApi({prompt: promptText});

        prompt.value = "";
        messages.value.push({role: "user", text: data.prompt});
        messages.value.push({role: "ai", text: data.response});

        await nextTick();
        promptField.value?.$el?.scrollIntoView({behavior: "smooth", block: "start"});
    } catch (error) {
        console.error(error);
    } finally {
        state.loading.response = false;
    }
};

watch(modalVisible, (visible) => {
    if (visible) {
        loadHistory();
    }
});
</script>

<template>
    <Drawer v-model:visible="modalVisible" position="right" header="Ask Albie" class="w-full max-w-2xl">
        <div class="flex w-full flex-col gap-4 overflow-y-auto">
            <p class="text-xs text-stone-600 dark:text-stone-400">
                Albie is an AI agent. Like all AI agents Albie’s responses may contain errors, hallucinations or
                incomplete reasoning. Do not rely on Albie’s responses for high-stakes decisions.
            </p>
            <template v-if="state.loading.history">
                <div class="flex flex-col items-center gap-2 py-6">
                    <ProgressSpinner />
                    <p class="text-center text-lg font-semibold">Loading history...</p>
                </div>
            </template>

            <template v-else>
                <div v-if="messages.length">
                    <div class="mb-4 flex justify-end">
                        <Button
                            v-tooltip.left="`Clear History`"
                            label="Clear History"
                            :icon="{icon: 'prime:trash'}"
                            severity="secondary"
                            size="small"
                            :loading="state.loading.history"
                            @click="clear"
                        />
                    </div>
                    <template v-for="(message, i) in messages" :key="i">
                        <div
                            class="mb-6 p-4"
                            :class="[
                                message.role === 'user'
                                    ? 'rounded-lg border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800'
                                    : 'border-l-4 border-stone-300 dark:border-stone-600',
                            ]"
                        >
                            <p v-if="message.role === 'user'">{{ message.text }}</p>
                            <div
                                v-else
                                class="prose prose-stone w-full max-w-full dark:prose-invert"
                                v-html="Str.of(md.render(message.text ?? 'There was an error.')).toString()"
                            ></div>
                        </div>
                    </template>
                </div>

                <form @submit.prevent="ask" class="relative">
                    <Textarea
                        ref="promptField"
                        id="albie_prompt"
                        v-model="prompt"
                        placeholder="What would you like to know about ASKr?"
                        class="w-full pr-14 text-base"
                        auto-resize
                    />
                    <Button
                        type="submit"
                        class="!absolute bottom-3 right-3 !p-0"
                        :loading="state.loading.response"
                        :icon="{icon: 'prime:send'}"
                    />
                </form>

                <div v-if="state.loading.response" class="flex flex-col items-center gap-2 py-6">
                    <ProgressSpinner />
                    <p class="text-center text-lg font-semibold">Asking Albie...</p>
                </div>
            </template>
        </div>
    </Drawer>
</template>

<style scoped></style>
